import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import bankService from "../../../services/bank.service";
import { readPagination } from "../../../utils/listPagination";
import "../../../styles/dashboard.css";
import "../../../styles/bankDashboard.css";

export default function BankDashboard() {
  const { auth } = useAuth();
  const user = auth?.user;
  const isAdmin = user?.role === "BANK_ADMIN";

  const [stats, setStats] = useState({
    records: 0,
    persons: 0,
    agencies: 0,
    files: 0,
    seized: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [recRes, tracRes, uploadRes, seizedRes] = await Promise.all([
          bankService.getRecords({ limit: 1 }),
          bankService.getTracingView(),
          bankService.listUploads(),
          bankService.getTraceCases({ limit: 1, view: "seized" }),
        ]);
        const records = readPagination(recRes?.data).total;
        const agencies = tracRes?.data?.data?.agencies?.length || 0;
        const files = uploadRes?.data?.data?.batches?.length || 0;
        const seized = readPagination(seizedRes?.data).total;

        let persons = 0;
        if (isAdmin) {
          const pRes = await bankService.getPersons();
          persons = pRes?.data?.data?.persons?.length || 0;
        }

        setStats({ records, persons, agencies, files, seized });
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isAdmin]);

  const statCards = [
    {
      to: "/bank/vehicle-search",
      icon: "📊",
      value: stats.records,
      title: "All records",
      desc: isAdmin
        ? "All uploaded records in this bank"
        : "Records you uploaded",
    },
    {
      to: "/bank/files",
      icon: "📄",
      value: stats.files,
      title: "Upload Repossession List",
      desc: "Excel batches uploaded by the bank",
    },
    {
      to: "/bank/tracing",
      icon: "📡",
      value: stats.agencies,
      title: "Linked Agencies",
      desc: "See linked agencies and live tracing",
    },
    {
      to: "/bank/seized-vehicles",
      icon: "✅",
      value: stats.seized,
      title: "Total seized vehicles",
      desc: "Bank-uploaded vehicles seized by linked agencies",
    },
    ...(isAdmin
      ? [
          {
            to: "/bank/persons",
            icon: "👥",
            value: stats.persons,
            title: "Bank / NBFC Executive",
            desc: "Add and manage bank users",
          },
        ]
      : []),
  ];

  const actionCards = [
    {
      to: "/bank/vehicle-search",
      icon: "🚗",
      title: "Find vehicles",
      desc: "Search by vehicle number or chassis number",
    },
    {
      to: "/bank/trace-cases",
      icon: "📋",
      title: "Trace Cases",
      desc: "Search vehicle, customer, loan or bank",
    },
    {
      to: "/bank/profile",
      icon: "👤",
      title: "Profile",
      desc: "View and update your profile",
    },
    ...(isAdmin
      ? [
          {
            to: "/bank/persons",
            icon: "👥",
            title: "Bank / NBFC Executive",
            desc: "Add and manage bank users",
          },
        ]
      : []),
  ];

  return (
    <div className="page bank-dashboard">
      <div className="dashboard-header bank-dashboard__header">
        <div>
          <h2>Welcome, {user?.name}</h2>
          <p className="muted bank-dashboard__meta">
            {user?.bank?.bankName} &nbsp;·&nbsp; {user?.role === "BANK_ADMIN" ? "Bank Admin" : "Bank Executive"}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="muted">Loading...</p>
      ) : (
        <div className="bank-dashboard__grid bank-dashboard__cards">
          {[...statCards, ...actionCards].map((card, index) => (
            <DashCard key={`${card.to}-${card.title}-${index}`} {...card} />
          ))}
        </div>
      )}
    </div>
  );
}

function DashCard({ to, icon, title, desc, value }) {
  return (
    <Link to={to} className="bank-dashboard__link">
      <div className="bank-dashboard__action">
        <div className="bank-dashboard__action-icon">{icon}</div>
        {value != null ? (
          <div className="bank-dashboard__stat-value">
            {Number(value).toLocaleString("en-IN")}
          </div>
        ) : null}
        <h4>{title}</h4>
        <p>{desc}</p>
      </div>
    </Link>
  );
}
